import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Landmark, Plus } from 'lucide-react'
import { Badge, Button, Card, EmptyState } from '@/components/ui'
import { useCommonAreaFees } from '@/hooks/useCommonAreaFees'
import { formatBaht } from '@/lib/money'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import type { Property } from '@/types'

interface PropertyCafFieldsProps {
  property: Property
}

/**
 * Condominium common-area-fee quick-add and effective-dated history.
 *
 * Kept separate from PropertyPricingFields so condominium properties can expose
 * CAF as a first-class property tab without duplicating the existing controls.
 */
export function PropertyCafFields({ property }: PropertyCafFieldsProps) {
  const { t } = useTranslation('properties')
  const { t: tc } = useTranslation('common')
  const { t: tf } = useTranslation('common_area_fees')
  const navigate = useNavigate()
  const showCaf = property.property_type === 'condominium'
  const { data: commonAreaFees } = useCommonAreaFees(showCaf ? property.id : 0, true)
  const { timezone } = useLocalizationConfig()

  if (!showCaf) return null

  // DAT-1: the account's local calendar day, not the UTC day a
  // `new Date().toISOString().slice(0,10)` read would give: that shifted a
  // fee's active/expired badge during the account's early morning hours
  // (e.g. Asia/Bangkok 00:00-07:00).
  const today = localToday(timezone)

  return (
    <section data-tour-target="caf" aria-labelledby="caf-heading">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-base-content/60" />
            <h3 id="caf-heading" className="text-lg font-semibold">
              {t('sections.common_area_fees')}
            </h3>
          </div>
          <Button
            size="sm"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => navigate(`/properties/${property.id}/common-area-fees/new`)}
          >
            {tf('actions.add_fee')}
          </Button>
        </div>

        {!commonAreaFees || commonAreaFees.length === 0 ? (
          <EmptyState
            icon={<Landmark className="w-12 h-12" />}
            title={tf('messages.no_fees')}
            description={tf('messages.no_fees_description')}
            action={
              <Button
                size="sm"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => navigate(`/properties/${property.id}/common-area-fees/new`)}
              >
                {tf('actions.add_fee')}
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr>
                  <th>{tf('labels.name')}</th>
                  <th>{tf('labels.fee_type')}</th>
                  <th>{tf('labels.calculation_method')}</th>
                  <th className="text-right">{tf('labels.rate')}</th>
                  <th>{tf('labels.effective_from')}</th>
                  <th>{tf('labels.effective_until')}</th>
                  <th>{tf('labels.status')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {commonAreaFees.map((fee) => {
                  const isActive =
                    fee.effective_from <= today &&
                    (fee.effective_until === null || fee.effective_until >= today)

                  return (
                    <tr key={fee.id}>
                      <td className="font-medium">{fee.name}</td>
                      <td>{tf(`fee_types.${fee.fee_type}`)}</td>
                      <td>{tf(`calculation_methods.${fee.calculation_method}`)}</td>
                      <td className="text-right">{formatBaht(Number(fee.rate))}</td>
                      <td>{fee.effective_from}</td>
                      <td>{fee.effective_until ?? '—'}</td>
                      <td>
                        <Badge variant={isActive ? 'success' : 'ghost'}>
                          {isActive ? tf('status.active') : tf('status.expired')}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            navigate(`/properties/${property.id}/common-area-fees/${fee.id}/edit`)
                          }
                        >
                          {tc('actions.edit')}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  )
}
